/**
 * DamageDetection3DResults
 *
 * Design: Engineering/Industrial analysis dashboard
 * - High-tech visual comparison
 * - Precise metric visualization (depth, dimensions)
 * - Detailed defect log with engineering controls
 */
import React from "react";
import { DamageDetection3DResults as DamageDetection3DResultsType } from "../../types";
import {
  FileCheck,
  Download,
  AlertTriangle,
  CheckCircle2,
  Target,
  Box,
  Maximize2,
  Clock,
  Hash,
  AlertCircle,
} from "lucide-react";

interface DamageDetection3DResultsProps {
  results: DamageDetection3DResultsType;
  annotatedOutputUrl?: string; // From job.annotated_output_url
  inputFileName?: string; // Original file name for display
}

export const DamageDetection3DResults: React.FC<
  DamageDetection3DResultsProps
> = ({ results, annotatedOutputUrl, inputFileName }) => {
  // Severity helpers matching backend
  const getSeverityConfig = (severity: string) => {
    switch (severity.toLowerCase()) {
      case "critical":
        return {
          color: "text-red-400",
          bg: "bg-red-500/20",
          border: "border-red-500/30",
          icon: <AlertTriangle className="w-4 h-4" />,
        };
      case "severe":
        return {
          color: "text-orange-400",
          bg: "bg-orange-500/20",
          border: "border-orange-500/30",
          icon: <AlertCircle className="w-4 h-4" />,
        };
      case "moderate":
        return {
          color: "text-yellow-400",
          bg: "bg-yellow-500/20",
          border: "border-yellow-500/30",
          icon: <AlertCircle className="w-4 h-4" />,
        };
      default:
        return {
          color: "text-emerald-400",
          bg: "bg-emerald-500/20",
          border: "border-emerald-500/30",
          icon: <CheckCircle2 className="w-4 h-4" />,
        };
    }
  };

  const getOverallStatus = () => {
    if (results.total_dents_found === 0)
      return { label: "No Damage", ...getSeverityConfig("minor") };
    if (results.damage_severity_distribution.critical > 0)
      return { label: "Critical Damage", ...getSeverityConfig("critical") };
    if (results.damage_severity_distribution.severe > 0)
      return { label: "Severe Damage", ...getSeverityConfig("severe") };
    if (results.damage_severity_distribution.moderate > 0)
      return { label: "Moderate Damage", ...getSeverityConfig("moderate") };
    return { label: "Minor Damage", ...getSeverityConfig("minor") };
  };

  const overall = getOverallStatus();

  // Image handling
  const beforeImageSrc = results.residual_image_before
    ? `data:image/png;base64,${results.residual_image_before}`
    : null;
  const afterImageSrc =
    annotatedOutputUrl ||
    (results.segmentation_image_after
      ? `data:image/png;base64,${results.segmentation_image_after}`
      : null);

  const sortedDents = [...(results.dents || [])].sort((a, b) => {
    const order = { critical: 0, severe: 1, moderate: 2, minor: 3 };
    return (
      (order[a.severity as keyof typeof order] ?? 4) -
      (order[b.severity as keyof typeof order] ?? 4)
    );
  });

  const handleDownloadReport = () => {
    const report = generateReport();
    const blob = new Blob([report], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `3d_damage_analysis_${results.request_id || Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const generateReport = () => {
    return `3D DAMAGE REPORT\nFile: ${inputFileName}\nDate: ${new Date().toISOString()}\nTotal Dents: ${
      results.total_dents_found
    }\n\n${sortedDents
      .map(
        (d) =>
          `Dent #${d.dent_id} [${d.severity.toUpperCase()}]\nType: ${
            d.dent_type
          }\nDepth: ${d.depth_mm.toFixed(
            3
          )}mm\nLocation: ${d.location.x.toFixed(1)}, ${d.location.y.toFixed(
            1
          )}, ${d.location.z.toFixed(1)}\n`
      )
      .join("\n")}`;
  };

  return (
    <div className="space-y-6">
      {/* Header Stat Card */}
      <div
        className={`relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700/50 p-6`}
      >
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-6">
            <div
              className={`w-16 h-16 rounded-2xl flex items-center justify-center border ${overall.border} ${overall.bg}`}
            >
              {results.total_dents_found === 0 ? (
                <CheckCircle2 className={`w-8 h-8 ${overall.color}`} />
              ) : (
                <AlertTriangle className={`w-8 h-8 ${overall.color}`} />
              )}
            </div>
            <div>
              <h2 className={`text-3xl font-bold ${overall.color}`}>
                {overall.label}
              </h2>
              <p className="text-slate-400 text-sm mt-1">
                {results.total_dents_found === 0
                  ? "Surface integrity verified"
                  : `${results.total_dents_found} anomalies detected`}
              </p>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleDownloadReport}
              className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-white text-sm font-medium transition-colors"
            >
              <Download className="w-4 h-4" />
              Report
            </button>
            {inputFileName && (
              <div className="flex items-center gap-2 px-4 py-2 bg-slate-800 rounded-lg border border-slate-600/50 text-slate-300 text-sm">
                <FileCheck className="w-4 h-4" />
                {inputFileName}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* No Damage State */}
      {results.total_dents_found === 0 ? (
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-12 text-center">
          <div className="w-24 h-24 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-12 h-12 text-emerald-400" />
          </div>
          <h3 className="text-2xl font-bold text-white mb-2">
            Components Verified
          </h3>
          <p className="text-slate-400 max-w-lg mx-auto">
            Automated 3D analysis confirms zero surface defects exceeding the
            configured threshold.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Severity Breakdown */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(results.damage_severity_distribution).map(
              ([level, count]) => {
                if (
                  level === "critical" ||
                  level === "severe" ||
                  level === "moderate" ||
                  level === "minor"
                ) {
                  const cfg = getSeverityConfig(level);
                  return (
                    <div
                      key={level}
                      className={`rounded-xl p-4 border ${cfg.border} bg-slate-800/50 relative overflow-hidden`}
                    >
                      <div className={`absolute top-0 right-0 p-3 opacity-10`}>
                        {cfg.icon}
                      </div>
                      <p className={`text-3xl font-bold ${cfg.color}`}>
                        {count}
                      </p>
                      <p className="text-slate-400 text-sm capitalize">
                        {level}
                      </p>
                    </div>
                  );
                }
                return null;
              }
            )}
          </div>

          {/* Visual Analysis */}
          {(beforeImageSrc || afterImageSrc) && (
            <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-6">
                <Target className="w-5 h-5 text-primary-400" />
                <h3 className="text-white font-semibold">
                  Defect Visualization
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {beforeImageSrc && (
                  <div className="space-y-2">
                    <div className="text-xs text-slate-400 uppercase tracking-wider font-semibold">
                      Original Scan
                    </div>
                    <div className="rounded-xl overflow-hidden border border-slate-700/50 bg-slate-900/50">
                      <img
                        src={beforeImageSrc}
                        alt="Original"
                        className="w-full h-64 object-contain"
                      />
                    </div>
                  </div>
                )}
                {afterImageSrc && (
                  <div className="space-y-2">
                    <div className="text-xs text-slate-400 uppercase tracking-wider font-semibold">
                      Defect Overlay
                    </div>
                    <div className="rounded-xl overflow-hidden border border-slate-700/50 bg-slate-900/50">
                      <img
                        src={afterImageSrc}
                        alt="Analyzed"
                        className="w-full h-64 object-contain"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Detailed Log */}
          <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl overflow-hidden">
            <div className="p-6 border-b border-slate-700/50 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <Hash className="w-5 h-5 text-primary-400" />
                <h3 className="text-white font-semibold">Defect Log</h3>
              </div>
              <span className="text-xs text-slate-500 font-mono">
                ID: {results.request_id}
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-900/50 text-slate-400">
                  <tr>
                    <th className="p-4 font-medium">ID</th>
                    <th className="p-4 font-medium">Severity</th>
                    <th className="p-4 font-medium">Metrics (Depth)</th>
                    <th className="p-4 font-medium">Dimensions</th>
                    <th className="p-4 font-medium">Coordinates</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700/30">
                  {sortedDents.map((dent) => {
                    const conf = getSeverityConfig(dent.severity);
                    return (
                      <tr key={dent.dent_id} className="hover:bg-slate-700/20">
                        <td className="p-4 font-mono text-slate-300">
                          #{dent.dent_id}
                        </td>
                        <td className="p-4">
                          <span
                            className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border ${conf.border} ${conf.bg} ${conf.color}`}
                          >
                            {conf.icon}
                            <span className="capitalize">{dent.severity}</span>
                          </span>
                        </td>
                        <td className="p-4 text-slate-300 font-mono">
                          {dent.depth_mm.toFixed(3)}mm
                          <span className="text-slate-500 text-xs ml-1">
                            ({dent.depth_microns.toFixed(0)}μm)
                          </span>
                        </td>
                        <td className="p-4 text-slate-400 text-xs">
                          <div className="flex items-center gap-1">
                            <Maximize2 className="w-3 h-3" />
                            {dent.dimensions.width.toFixed(1)} x{" "}
                            {dent.dimensions.height.toFixed(1)}
                          </div>
                        </td>
                        <td className="p-4 text-slate-500 text-xs font-mono">
                          x:{dent.location.x.toFixed(1)}, y:
                          {dent.location.y.toFixed(1)}, z:
                          {dent.location.z.toFixed(1)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-end text-xs text-slate-500">
        <div className="flex items-center gap-2">
          <Clock className="w-3 h-3" />
          Processed in {results.processing_time_seconds.toFixed(2)}s
        </div>
      </div>
    </div>
  );
};
